import { type EvmChainId, type EvmChainType, type EvmConfigType, type EvmConnectorId } from '@/service/EVM/config/types';
import { http } from '@wagmi/vue';
import type { HttpTransport } from 'viem';
import { mainnet, polygon } from 'viem/chains';
import { xsollaZkChain } from '@/service/EVM/chains';
import { rootChainManagerProxyAbi } from '@/service/EVM/abi/bridge';
import { wethAbi } from '@/service/EVM/abi/weth';
import { isEvmErc1155, isEvmErc721 } from '@/service/utils';
import { ERC1155Abi, ERC721Abi } from '@/service/EVM/abi/type';

import { DEFI_NATIVE_ADDRESS, NULL_ADDRESS, NULL_HASH } from '@/service/EVM/consts';
import { EvmItemType } from '@/service/EVM/types';

export const MARKETPLACE_NAME = 'default';
export const NETWORK_NAME = {
    MAINNET: 'eth',
    POLYGON: 'matic',
    XSOLLA_ZK: 'xsollaZk'
} as const;

export const chains = [
    mainnet,
    polygon,
    xsollaZkChain
] as const;

export const CONNECTOR_IDS = {
    METAMASK: 'metaMaskSDK',
    WALLET_CONNECT: 'walletConnect',
    COINBASE: 'coinbaseWalletSDK',
    ZK_SSO: 'zksync-sso'
} as const;

export const CONNECTORS_CONFIG = {
    [CONNECTOR_IDS.METAMASK]: {
        id: CONNECTOR_IDS.METAMASK,
        extensionId: 'io.metamask',
        name: 'MetaMask',
        icon: 'logo/metamask'
    },
    [CONNECTOR_IDS.WALLET_CONNECT]: {
        id: CONNECTOR_IDS.WALLET_CONNECT,
        name: 'WalletConnect',
        icon: 'logo/wallet-connect'
    },
    [CONNECTOR_IDS.COINBASE]: {
        id: CONNECTOR_IDS.COINBASE,
        name: 'Coinbase Wallet',
        icon: 'logo/coinbase'
    },
    [CONNECTOR_IDS.ZK_SSO]: {
        id: CONNECTOR_IDS.ZK_SSO,
        name: 'Xsolla ZK Smart Wallet',
        icon: 'chains/XSOLLAZK'
    }
} as const satisfies Record<EvmConnectorId, {
    id: EvmConnectorId;
    extensionId?: string;
    name: string;
    icon: SvgNameKeysSlash;
}>;

export const chainMapById = chains.reduce((acc, chain) => {
    acc[chain.id] = chain;
    return acc;
}, {} as Record<EvmChainId, EvmChainType>);

export const chainIds = chains.map(chain => chain.id);

const httpStatusDescriptions: Record<number, string> = {
    400: 'Bad Request',
    401: 'Unauthorized',
    403: 'Forbidden',
    404: 'Not Found',
    405: 'Method Not Allowed',
    415: 'Unsupported Media Type',
    429: 'Too Many Requests',
    500: 'Internal Server Error',
    502: 'Bad Gateway',
    503: 'Service Unavailable',
    504: 'Gateway Timeout'
};

const triggeredRpcErrors = new Set<string>();

export function clearRpcErrors() {
    triggeredRpcErrors.clear();
}

export function createTransports(eventEmitter: EvmEventEmitter): Record<EvmChainId, HttpTransport> {
    return chainIds.reduce((acc, id) => {
        acc[id] = http(undefined, {
            retryCount: 0,
            timeout: 10000,
            retryDelay: 1000,
            onFetchResponse: async (response: Response) => {
                const key = `${response.url}-${response.status}`;
                if (!response.ok && !triggeredRpcErrors.has(key)) {
                    triggeredRpcErrors.add(key);

                    let message = httpStatusDescriptions[response.status] || 'Unknown error';

                    try {
                        const text = await response.text();
                        const match = text.match(/message:\s*(.+?)(,|$)/i);
                        if (match?.[1]) {
                            message = match[1].trim();
                        }
                    }
                    catch (e) {
                        console.warn('Failed to parse text from RPC response', e);
                    }

                    await eventEmitter.emit(
                        'rpcError',
                        async () => message,
                        message
                    );
                }
            }
        });
        return acc;
    }, {} as Record<EvmChainId, HttpTransport>);
}

const scanner = {
    [NETWORK_NAME.MAINNET]: {
        tx: 'https://etherscan.io/tx/',
        address: 'https://etherscan.io/address/',
        name: 'etherscan',
        icon: 'etherscan'
    },
    [NETWORK_NAME.POLYGON]: {
        tx: 'https://polygonscan.com/tx/',
        address: 'https://polygonscan.com/address/',
        name: 'polygonscan',
        icon: ''
    },
    [NETWORK_NAME.XSOLLA_ZK]: {
        tx: 'https://x.la/explorer/tx/',
        address: 'https://x.la/explorer/address/',
        name: 'xsollaZk',
        icon: 'XSOLLAZK'
    }
} as const;

export const evmConfig = {
    abi: {
        type: {
            [EvmItemType.ERC721]: ERC721Abi,
            [EvmItemType.ERC1155]: ERC1155Abi
        }
    },
    contract: {
        bridge: {
            weth: {
                address: '0xA0c68C638235ee32657e8f720a23ceC1bFc77C77',
                abi: rootChainManagerProxyAbi,
                chainId: mainnet.id,
                version: '1'
            }
        },
        wrap: { // TODO убрать
            weth: {
                [mainnet.id]: {
                    address: '0xC02aaA39b223FE8D0A0e5C4F27eAD9083C756Cc2',
                    abi: wethAbi,
                    chainId: mainnet.id
                },
                [polygon.id]: {
                    address: '0x0d500B1d8E8eF31E21C99d1Db9A6444d3ADf1270',
                    abi: wethAbi,
                    chainId: polygon.id
                },
                [xsollaZkChain.id]: {
                    address: '0x58ce9155AcE85721934b03cc764040cAEFbA628E',
                    abi: wethAbi,
                    chainId: xsollaZkChain.id
                }
            }
        }
    },
    links: {
        scanner
    },
    constants: {
        NETWORK_NAME,
        MARKETPLACE_NAME,
        NULL_ADDRESS,
        NULL_HASH,
        DEFI_NATIVE_ADDRESS
    },
    map: {
        chainCurrencyMap: {
            [mainnet.id]: NETWORK_NAME.MAINNET,
            [polygon.id]: NETWORK_NAME.POLYGON,
            [xsollaZkChain.id]: NETWORK_NAME.XSOLLA_ZK
        },
        chainMapById,
        chainMap: {
            [NETWORK_NAME.MAINNET]: mainnet,
            [NETWORK_NAME.POLYGON]: polygon,
            [NETWORK_NAME.XSOLLA_ZK]: xsollaZkChain
        }
    },
    provider: {
        chainIds
    },
    connector: {
        chains,
        config: CONNECTORS_CONFIG,
        methods: {
            clearRpcErrors
        }
    },
    utils: {
        isEvmErc721,
        isEvmErc1155
    }
} as const satisfies EvmConfigType;
